import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import type { RentedSubscription, Subscription } from "common/types";

import { get } from "../helpers/HTTP";
import getQueryKeys from "./getQueryKeys";

interface EstimateRenewal {
  nextBillingAt: number;
  amountDue: number;
  pricePerItem: number;
}

async function workspaceEstimateRenewalRequest(
  workspaceId: string,
  subscriptionId: string,
): Promise<EstimateRenewal> {
  const { estimate } =
    await get(`workspaces/${workspaceId}/subscriptions/${subscriptionId}/estimates/renew
`);
  const {
    subscription_estimate: subscriptionEstimate,
    invoice_estimate: invoiceEstimate,
  } = estimate;

  const perUnitLineItem = invoiceEstimate.line_items.find(
    ({ pricing_model }: { pricing_model: string }) =>
      pricing_model === "per_unit",
  );

  return {
    nextBillingAt: subscriptionEstimate.next_billing_at,
    amountDue: invoiceEstimate.amount_due,
    pricePerItem: perUnitLineItem ? perUnitLineItem.unit_amount : 0,
  };
}

export default function useWorkspaceEstimateRenewal(
  subscription: Subscription | RentedSubscription,
) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { data: estimateRenewal, isLoading: isLoadingEstimateRenewal } =
    useQuery({
      queryKey: getQueryKeys(workspaceId).estimate.renew(subscription),
      queryFn: () =>
        workspaceEstimateRenewalRequest(workspaceId, subscription?.id),
      refetchOnMount: false,
      enabled:
        !!subscription &&
        subscription.status !== "cancelled" &&
        subscription.status !== "non_renewing",
    });

  return {
    estimateRenewal,
    isLoadingEstimateRenewal,
  };
}
